import { type IWrappedProperty, type IWrappedPropertyArray, wrapSchema } from '@witkac/jschema';
import type { IWrapOptions } from '@witkac/jschema/lib/WrappedProperty/wrapSchema';
import {
  computed,
  getCurrentInstance,
  nextTick,
  watch,
  type ComputedRef,
  type ExtractPropTypes,
  type PropType
} from 'vue';

import { defaults } from './defaults';
import { generateInputIds, type IUseIds } from './id';
import { withDisabledProps, storiesDisabledArgs } from './props/disabled';
import { withInputDescriptionProps, storiesInputDescriptionArgs } from './props/inputDescription';
import { withLabelProps, storiesLabelArgs } from './props/label';
import { withSchemaDisplayConfigProps, storiesSchemaDisplayConfigArgs } from './props/schemaDisplayConfig';
import { useRegisterChild } from './registerChild';
import type { ComponentInstance } from '../typings/component';
import { disabledVariantList, type DisabledVariantType } from '../typings/disabledVariantType';

export const withSchemaDisabledVariantProps = {
  /**
   * Sposób działania pola `disabled`.
   * `hidden` - pole zostaje ukryte
   * `readonly` - pole jest widoczne, ale nie można go kliknąć
   */
  disabledVariant: {
    type: String as PropType<DisabledVariantType>,
    default: defaults.inputs.disabledVariant
  }
} as const;

/**
 * Konfiguracja dla storybooka
 */
export const storiesSchemaDisabledVariantArgs = {
  args: {
    disabledVariant: defaults.inputs.disabledVariant
  },

  argTypes: {
    disabledVariant: {
      options: disabledVariantList,
      control: { type: 'select' }
    }
  }
};

/** Props-y do użycia z jSchema */
export const withSchemaProps = {
  ...withDisabledProps,
  ...withInputDescriptionProps,
  ...withLabelProps,
  ...withSchemaDisplayConfigProps,
  ...withSchemaDisabledVariantProps,

  /** Typ domyślnego modelu */
  defaultModelType: { type: String, default: defaults.schema.defaultModelType },

  /** Model */
  model: {
    default: (props: { defaultModelType: string }) => emptySchemaModel({ type: props.defaultModelType }),
    type: Object as PropType<IWrappedProperty | IWrappedPropertyArray>
  },

  /** Placeholder */
  placeholder: { type: String }
} as const;

export const storiesSchemaArgs = {
  args: {
    ...storiesLabelArgs.args,
    ...storiesDisabledArgs.args,
    ...storiesInputDescriptionArgs.args,
    ...storiesSchemaDisplayConfigArgs.args,
    ...storiesSchemaDisabledVariantArgs.args,

    placeholder: ''
  },

  argTypes: {
    ...storiesLabelArgs.argTypes,
    ...storiesDisabledArgs.argTypes,
    ...storiesInputDescriptionArgs.argTypes,
    ...storiesSchemaDisplayConfigArgs.argTypes,
    ...storiesSchemaDisabledVariantArgs.argTypes,

    placeholder: {
      control: { type: 'text' }
    }
  }
};

/** Opcjonalne/dodatkowe props-y do użycia z jSchema */
export const withSchemaOptionalProps = {
  /** Opcja szybkiego czyszczenia inputa */
  clearable: { type: Boolean, default: undefined }
} as const;

export const storiesSchemaOptionalArgs = {
  args: {
    clearable: false
  },

  argTypes: {
    clearable: {
      control: { type: 'boolean' }
    }
  }
};

/** Emit-y do użycia z jSchema */
export const withSchemaEmits = {
  change: null,
  clear: null,
  blur: null,
  input: null,
  focus: null
} as const;

export type ISchemaProps = ExtractPropTypes<typeof withSchemaProps>;
type ISchemaInstance = ComponentInstance<typeof withSchemaProps, typeof withSchemaEmits>;

/** Props-y jSchema potrzebne do `aria-describedby` etc. */
export interface IUseSchemaAriaData {
  hasLabel: ComputedRef<boolean>;
  hasDescription: ComputedRef<boolean>;
  error: ComputedRef<string | null | undefined>;
  warning: ComputedRef<string | undefined>;
  success: ComputedRef<string | undefined>;
}

/** Props-y do użycia z jSchema */
export interface IUseSchema extends IUseSchemaAriaData {
  description: ComputedRef<string | undefined>;
  disabled: ComputedRef<boolean>;
  hasValue: ComputedRef<boolean>;
  dirtyWithoutErrors: ComputedRef<boolean>;
  label: ComputedRef<string | undefined>;
  required: ComputedRef<boolean>;

  onBlur: () => void;
  onChange: () => void;
  onClear: () => void;
  onFocus: () => void;
  onInput: () => void;
  triggerValidation: () => void;
  getSchemaClasses: (prefix: string) => Record<string, boolean>;
}

/**
 * Wykorzystanie jSchema wraz z id komponentu
 * @param props
 */
export function useSchemaWithIds(prefix?: string, props?: ISchemaProps): IUseSchema & IUseIds {
  const schema = useSchema(props);

  return {
    ...schema,
    ...generateInputIds(prefix, schema)
  };
}

/**
 * Opcje dla composable `useSchema`
 *
 * @export
 * @interface UseSchemaOptions
 */
export interface UseSchemaOptions {
  /**
   * Slot dla labela
   *
   * @type {string}
   * @memberof UseSchemaOptions
   */
  labelSlot: string;

  /**
   * Slot dla opisu
   *
   * @type {string}
   * @memberof UseSchemaOptions
   */
  descriptionSlot: string;
}

const defaultSchemaOptions: UseSchemaOptions = {
  labelSlot: 'default',
  descriptionSlot: 'description'
};

/**
 * Wykorzystanie jSchema
 * @param props
 */
export function useSchema(props?: ISchemaProps, options?: Partial<UseSchemaOptions>): IUseSchema {
  options = { ...defaultSchemaOptions, ...options };

  const instance = getCurrentInstance() as ISchemaInstance;
  if (!props && instance) {
    props = instance.props;
  }
  useRegisterChild(props?.model);

  const label = computed(() => props?.label?.toString() || props?.model.$title?.toString());
  const description = computed<string | undefined>(
    () => props?.description?.toString() || props?.model?.$description?.toString()
  );
  const hasLabel = computed(
    () => !!((label.value || instance.slots[options!.labelSlot!] || instance.slots.label) && props?.displayLabel)
  );
  const hasDescription = computed(
    () => !!((description.value || instance.slots[options!.descriptionSlot!]) && props?.displayDescription)
  );
  const disabled = computed(() => props?.disabled || !!props?.model?.$readOnly || !!props?.model.$disabled);
  const error = computed(() => (props?.displayValidation ? props?.model?.$error : undefined));
  const warning = computed(() => (props?.displayValidation ? props?.model.$warnings?.[0] : undefined));
  const dirtyWithoutErrors = computed(() => dirty.value && !error.value && !warning.value);
  const displaySuccess = computed(() => !!props?.displaySuccess);
  const isSuccess = computed(
    () =>
      (!required.value && dirtyWithoutErrors.value && displaySuccess.value && hasValue.value) ||
      (required.value && dirtyWithoutErrors.value && displaySuccess.value)
  );
  const success = computed(() =>
    isSuccess.value && props?.displayValidation ? defaults.inputMessage.successText : undefined
  );
  const dirty = computed(() => !!props?.model?.$dirty);
  const hasValue = computed(
    () => !!props?.model?.$value && (!Array.isArray(props.model.$value) || !!props.model.$value.length)
  );
  const required = computed(() => {
    if (!props?.model) {
      return false;
    }

    if (props.model.$schema.fieldRequired) {
      return true;
    }

    return props.model.$schema.type === 'array' && (props.model.$schema.minItems ?? 0) > 0;
  });

  /**
   * Obsługa change
   */
  function onChange(): void {
    instance?.emit('change', props?.model.$model);
  }

  function getSchemaClasses(prefix: string): Record<string, boolean> {
    return {
      [`${prefix}--disabled`]: disabled.value,
      [`${prefix}--schema-dirty`]: dirty.value,
      [`${prefix}--schema-error`]: dirty.value && !!error.value,
      [`${prefix}--schema-warning`]: dirty.value && !!warning.value,
      [`${prefix}--schema-success`]:
        (!required.value && dirtyWithoutErrors.value && displaySuccess.value && hasValue.value) ||
        (required.value && dirtyWithoutErrors.value && displaySuccess.value)
    };
  }

  /**
   * Obsługa clear
   */
  function onClear(): void {
    if (props?.model) {
      if (Array.isArray(props.model.$value)) {
        (props.model as IWrappedPropertyArray).$setData([], undefined, true);
      } else {
        (props.model as IWrappedProperty).$value = '';
      }
    }
    instance?.emit('clear');
    triggerValidation();
  }

  /**
   * Obsługa blur
   */
  function onBlur(): void {
    if (props?.model) {
      props.model.$hasFocus = false;
    }
    instance?.emit('blur');
  }

  /**
   * Obsługa focus
   */
  function onFocus(): void {
    if (props?.model) {
      props.model.$hasFocus = true;
    }
    instance?.emit('focus');
  }

  /**
   * Obsługa input
   */
  function onInput(): void {
    instance?.emit('input', props?.model?.$model);
  }

  /**
   * Uruchamia walidację pola
   */
  function triggerValidation(): void {
    onFocus();
    nextTick(onBlur);
    onChange();
  }

  return {
    description,
    hasLabel,
    hasDescription,
    disabled,
    error,
    hasValue,
    label,
    warning,
    success,
    required,
    dirtyWithoutErrors,

    onBlur,
    onChange,
    onClear,
    onFocus,
    onInput,
    getSchemaClasses,
    triggerValidation
  };
}

/**
 * Prosta schema do zwrócenia modelu
 *
 * @export
 * @param {*} [field={ type: 'string' }]
 * @param {Partial<IWrapOptions>} [options={}]
 * @return {(IWrappedProperty | IWrappedPropertyArray)}
 */
export function emptySchemaModel(
  field: any = { type: 'string' },
  options: Partial<IWrapOptions> = {}
): IWrappedProperty | IWrappedPropertyArray {
  return wrapSchema({ properties: { field }, type: 'object' }, undefined, options).field;
}

/**
 * Prosta schema do zwrócenia modelu bazująca na schemie przekazanej do externala
 *
 * @export
 * @param {string} [type='string']
 * @param {Partial<IWrapOptions>} options
 * @return {(IWrappedProperty | IWrappedPropertyArray)}
 */
export function useExternalSchemaProperties(
  options: Partial<IWrapOptions>,
  type = 'string'
): IWrappedProperty | IWrappedPropertyArray {
  return emptySchemaModel(
    {
      type,
      disabled: '$external.disabled',
      fieldRequired: '$external.fieldRequired',
      readOnly: '$external.readOnly',
      validFunc: '$external.validFunc'
    },
    options
  );
}
/**
 * Przygotowanie wewnętrznej schemy
 * @param externalModel
 */
export function prepareInternalSchema(
  externalModel: IWrappedProperty<any, any, any> | undefined
): IWrappedProperty<any, any, any> {
  return useExternalSchemaProperties({
    ...(externalModel as any)?._options,
    external: externalModel?.$schema,
    validationMessages: {
      ...(externalModel as any)?._options.validationMessages,
      ...(externalModel as any)?._internal.schema.validationMessages
    }
  });
}

/**
 *  Łączy walidację modelu wewnętrznego z zewnętrznym
 *
 * @export
 * @param {IWrappedProperty} internalSchema
 * @param {IWrappedProperty} [externalSchema]
 * @returns {void}
 */
export function linkModelValidations(internalSchema: IWrappedProperty, externalSchema?: IWrappedProperty): void {
  if (!externalSchema) {
    return;
  }

  watch(
    () => externalSchema.$error,
    newValue => {
      if (newValue) {
        internalSchema.$validateAll();
        internalSchema.$checkAll();
      }
    },
    { immediate: true }
  );
}
